import { useEffect, useRef, useCallback } from "react";

interface UsePollingOptions {
  /** Polling interval in ms when tab is visible */
  activeInterval: number;
  /** Polling interval in ms when tab is hidden */
  backgroundInterval: number;
  /** Whether polling is enabled */
  enabled?: boolean;
}

/**
 * Generic polling hook with Page Visibility API support.
 * Pauses/slows polling when the tab is hidden.
 */
export function usePolling(
  callback: () => void | Promise<void>,
  options: UsePollingOptions
) {
  const { activeInterval, backgroundInterval, enabled = true } = options;
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const callbackRef = useRef(callback);

  // Keep callback ref up to date
  callbackRef.current = callback;

  const schedule = useCallback(() => {
    const interval = document.hidden ? backgroundInterval : activeInterval;
    timerRef.current = setTimeout(async () => {
      try {
        await callbackRef.current();
      } catch {
        // Swallow polling errors
      }
      if (enabled) schedule();
    }, interval);
  }, [activeInterval, backgroundInterval, enabled]);

  useEffect(() => {
    if (!enabled) return;

    // Run immediately on mount
    Promise.resolve(callbackRef.current()).catch(() => {});

    schedule();

    // Reschedule when visibility changes
    const onVisibilityChange = () => {
      clearTimeout(timerRef.current);
      schedule();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearTimeout(timerRef.current);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [enabled, schedule]);
}
